import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, ArrowRight, Clock, Users, ChevronRight, Check, X, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function NotificationsAjustement({ cpId }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showBonsDialog, setShowBonsDialog] = useState(null);
  const [allBons, setAllBons] = useState([]);
  const [loadingBons, setLoadingBons] = useState(false);

  const fetchNotifs = async () => {
    try {
      const url = cpId
        ? `${API_URL}/api/crm/notifications-ajustement?cp_id=${cpId}&statut=en_attente`
        : `${API_URL}/api/crm/notifications-ajustement?statut=en_attente`;
      const res = await axios.get(url);
      if (res.status === 200) {
        const data = res.data;
        setNotifs(data.notifications || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchNotifs(); }, [cpId]);

  const handleAjuster = async (notifId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/notifications-ajustement/${notifId}/ajuster`, null);
      if (res.status !== 200) throw new Error('Erreur');
      const data = res.data;
      toast.success(`Dates ajustées ! Nouvelles dates : ${data.nouvelles_dates?.slice(0, 3).join(', ')}${data.nouvelles_dates?.length > 3 ? '...' : ''}`);
      setActionDialog(null);
      fetchNotifs();
    } catch (e) { toast.error('Erreur lors de l\'ajustement'); }
    finally { setProcessing(false); }
  };

  const handleIgnorer = async (notifId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/notifications-ajustement/${notifId}/ignorer`, null);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success('Notification ignorée');
      setActionDialog(null);
      fetchNotifs();
    } catch (e) { toast.error('Erreur'); }
    finally { setProcessing(false); }
  };

  const handleShowBons = async (notif) => {
    setShowBonsDialog(notif);
    setLoadingBons(true);
    try {
      // Charger tous les bons de travail actifs pour cette équipe
      const res = await axios.get(`${API_URL}/api/crm/bons-travail?chef_equipe_id=${notif.chef_equipe_id}&statut=planifie,en_cours,actif,brouillon`);
      if (res.status === 200) {
        const data = res.data;
        setAllBons((data.bons_travail || []).filter(b => b.id !== notif.prochain_bon_id && b.id !== notif.bon_termine_id));
      }
    } catch (e) { console.error(e); }
    finally { setLoadingBons(false); }
  };

  if (loading) return null;
  if (notifs.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="notifications-ajustement">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-600" />
          Ajustements de planification
          <Badge className="bg-amber-100 text-amber-800 text-xs">{notifs.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {notifs.map(notif => {
          const enAvance = notif.jours_decalage < 0;
          const enRetard = notif.jours_decalage > 0;
          return (
            <div key={notif.id} className="bg-white rounded-lg border p-3 space-y-2" data-testid={`notif-ajustement-${notif.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Users className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="font-medium text-sm">{notif.chef_equipe_nom}</span>
                    <Badge className={`text-xs ${enAvance ? 'bg-green-100 text-green-800' : enRetard ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                      {enAvance ? `${Math.abs(notif.jours_decalage)}j en avance` : enRetard ? `${notif.jours_decalage}j en retard` : 'A temps'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    <span className="font-medium">{notif.bon_termine_client_nom}</span> terminé
                    <ArrowRight className="w-3 h-3 inline mx-1" />
                    Prochain : <span className="font-medium">{notif.prochain_bon_client_nom}</span> ({notif.prochain_bon_dossier_nom})
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Dates actuelles : {(notif.prochain_bon_dates || []).slice(0, 3).join(', ')}{(notif.prochain_bon_dates || []).length > 3 ? '...' : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => setActionDialog(notif)} data-testid={`btn-action-ajustement-${notif.id}`}>
                  Décider <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          );
        })}

        {/* Dialog de décision */}
        {actionDialog && (
          <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  Ajustement de planification
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                  <p><strong>Equipe :</strong> {actionDialog.chef_equipe_nom}</p>
                  <p><strong>Chantier terminé :</strong> {actionDialog.bon_termine_client_nom} — {actionDialog.bon_termine_dossier_nom}</p>
                  <p><strong>Fin prévue :</strong> {actionDialog.date_fin_prevue} | <strong>Fin réelle :</strong> {actionDialog.date_fin_reelle}</p>
                  <div className="mt-2 pt-2 border-t">
                    <p><strong>Prochain chantier :</strong> {actionDialog.prochain_bon_client_nom} — {actionDialog.prochain_bon_dossier_nom}</p>
                    <p><strong>Dates actuelles :</strong> {(actionDialog.prochain_bon_dates || []).join(', ')}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    className="w-full justify-start gap-3 h-auto py-3 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleAjuster(actionDialog.id)}
                    disabled={processing}
                    data-testid="btn-ajuster-dates"
                  >
                    <Check className="w-5 h-5 shrink-0" />
                    <div className="text-left">
                      <p className="font-semibold">Ajuster les dates automatiquement</p>
                      <p className="text-xs opacity-80">
                        {actionDialog.jours_decalage < 0
                          ? `Avancer de ${Math.abs(actionDialog.jours_decalage)} jour(s)`
                          : actionDialog.jours_decalage > 0
                            ? `Décaler de ${actionDialog.jours_decalage} jour(s)`
                            : 'Aucun changement nécessaire'}
                      </p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-auto py-3 border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => handleShowBons(actionDialog)}
                    disabled={processing}
                    data-testid="btn-inserer-client"
                  >
                    <UserPlus className="w-5 h-5 shrink-0" />
                    <div className="text-left">
                      <p className="font-semibold">Insérer un autre client entre les deux</p>
                      <p className="text-xs opacity-70">Voir les bons de travail disponibles pour cette équipe</p>
                    </div>
                  </Button>

                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-auto py-3 text-slate-500"
                    onClick={() => handleIgnorer(actionDialog.id)}
                    disabled={processing}
                    data-testid="btn-ignorer-ajustement"
                  >
                    <X className="w-5 h-5 shrink-0" />
                    <div className="text-left">
                      <p className="font-semibold">Ignorer</p>
                      <p className="text-xs opacity-70">Ne rien changer aux dates</p>
                    </div>
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Dialog pour voir les bons disponibles */}
        {showBonsDialog && (
          <Dialog open={!!showBonsDialog} onOpenChange={() => setShowBonsDialog(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Bons de travail de l'équipe de {showBonsDialog.chef_equipe_nom}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 mb-3">
                Sélectionnez un bon à insérer dans le créneau libéré. Vous pourrez ensuite ajuster ses dates manuellement dans le dossier du client.
              </p>
              {loadingBons ? (
                <div className="text-center py-8 text-slate-400">Chargement...</div>
              ) : allBons.length === 0 ? (
                <div className="text-center py-8 text-slate-400">Aucun autre bon de travail planifié pour cette équipe.</div>
              ) : (
                <div className="space-y-2">
                  {allBons.map(bon => (
                    <div key={bon.id} className="border rounded-lg p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => {
                        // Navigate to the client's dossier
                        window.open(`/crm/clients/${bon.client_id || ''}?dossier=${bon.dossier_id}&bon=${bon.id}`, '_blank');
                        handleIgnorer(showBonsDialog.id);
                        setShowBonsDialog(null);
                      }}
                      data-testid={`bon-insert-${bon.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{bon.client_nom || 'Client'} — {bon.dossier_nom || bon.description_travaux?.substring(0, 50) || 'Projet'}</p>
                          <p className="text-xs text-slate-500">
                            {bon.type_travaux === 'exterieur' ? 'Ext.' : 'Int.'} |
                            Dates : {(bon.dates_chantier || []).slice(0, 3).join(', ')}{(bon.dates_chantier || []).length > 3 ? '...' : '' }
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBonsDialog(null)}>Fermer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
